import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import Typography from '@mui/material/Typography';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import { Markdown } from '@tiptap/markdown';
import { mergeAttributes } from '@tiptap/core';
import { ResizableImage } from 'tiptap-extension-resizable-image';
import 'tiptap-extension-resizable-image/styles.css';
import { Box, TextField, CircularProgress, Button, useTheme } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { sendApiRequest } from 'lib/api/public';
import PlaceholderChooser from 'components/common/PlaceholderChooser';
import Modal from 'components/common/Modal';
import notify from 'lib/notify';
import { uploadFileRequest, readFile } from 'lib/api/public';
import { DOMSerializer } from '@tiptap/pm/model';
import { sanitizeHtml } from 'lib/sanitizeHtml';
import mentionSuggestion from './mentionSuggestion';
import { mentionChipSx } from './mentionStyles';

const isHtmlContent = (value: string) => {
  const trimmed = (value || '').trim();
  return /^<[a-z!/?]/i.test(trimmed);
};

const isMsWordClipboardHtml = (html: string) => /<o:|<w:|<mso:|<v:|<o:p|class=["']?Mso|mso-/i.test(html);

const BULLET_PREFIX_RE = /^\s*(?:[•·●○▪▸►‣∙・]|&bull;|&#8226;)\s*/;

/** Strip Word clipboard junk that TipTap otherwise turns into hard line breaks / fake lists. */
const cleanMsWordHtml = (html: string): string => {
  let cleanHtml = html;

  cleanHtml = cleanHtml.replace(/<!--[\s\S]*?-->/g, '');
  cleanHtml = cleanHtml.replace(/<\/?o:[^>]*>/gi, '');
  cleanHtml = cleanHtml.replace(/<\/?w:[^>]*>/gi, '');
  cleanHtml = cleanHtml.replace(/<\/?mso:[^>]*>/gi, '');
  cleanHtml = cleanHtml.replace(/<\/?v:[^>]*>/gi, '');
  cleanHtml = cleanHtml.replace(/<o:p\s*\/?>|<\/o:p>/g, '');
  cleanHtml = cleanHtml.replace(/\s*style\s*=\s*["'][^"']*["']/gi, '');
  cleanHtml = cleanHtml.replace(/\s*class\s*=\s*["'][^"']*["']/gi, '');

  // Word soft-wraps often arrive as <br> mid-sentence; keep only intentional blank lines.
  cleanHtml = cleanHtml.replace(/(?:<br\s*\/?>\s*){2,}/gi, '</p><p>');
  cleanHtml = cleanHtml.replace(/<br\s*\/?>/gi, ' ');

  cleanHtml = cleanHtml.replace(/&nbsp;/gi, ' ');
  cleanHtml = cleanHtml.replace(/[\r\n]+/g, ' ');
  cleanHtml = cleanHtml.replace(/[ \t]+/g, ' ');

  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(cleanHtml, 'text/html');

    // Unwrap spans Word wraps every run in.
    let span: HTMLSpanElement | null;
    while ((span = doc.body.querySelector('span'))) {
      const parent = span.parentNode;
      if (!parent) break;
      while (span.firstChild) {
        parent.insertBefore(span.firstChild, span);
      }
      parent.removeChild(span);
    }

    // Drop empty paragraphs / divs.
    doc.body.querySelectorAll('p, div').forEach(el => {
      if (!el.textContent?.trim() && !el.querySelector('img')) {
        el.remove();
      }
    });

    // Turn "• item" paragraphs into a real list (Word often pastes lists this way).
    const blocks = Array.from(doc.body.children);
    let i = 0;
    while (i < blocks.length) {
      const el = blocks[i];
      if (!(el instanceof HTMLElement) || el.tagName !== 'P' || !BULLET_PREFIX_RE.test(el.textContent || '')) {
        i += 1;
        continue;
      }

      const ul = doc.createElement('ul');
      let j = i;
      while (j < blocks.length) {
        const itemEl = blocks[j];
        if (
          !(itemEl instanceof HTMLElement) ||
          itemEl.tagName !== 'P' ||
          !BULLET_PREFIX_RE.test(itemEl.textContent || '')
        ) {
          break;
        }

        const li = doc.createElement('li');
        li.innerHTML = itemEl.innerHTML;
        const stripLeadingBullet = (node: Node): boolean => {
          if (node.nodeType === Node.TEXT_NODE && node.textContent) {
            if (BULLET_PREFIX_RE.test(node.textContent)) {
              node.textContent = node.textContent.replace(BULLET_PREFIX_RE, '');
              return true;
            }
            return false;
          }

          for (const child of Array.from(node.childNodes)) {
            if (stripLeadingBullet(child)) {
              return true;
            }
          }

          return false;
        };
        stripLeadingBullet(li);
        ul.appendChild(li);
        j += 1;
      }

      el.replaceWith(ul);
      for (let k = i + 1; k < j; k += 1) {
        blocks[k].remove();
      }
      blocks.splice(i, j - i, ul);
      i += 1;
    }

    cleanHtml = doc.body.innerHTML;
  }

  cleanHtml = cleanHtml.replace(/<p[^>]*>\s*<\/p>/gi, '');
  cleanHtml = cleanHtml.replace(/[ \t]{2,}/g, ' ');
  cleanHtml = cleanHtml.replace(/>\s+</g, '><');

  return cleanHtml.trim();
};

const mentionExtension = Mention.configure({
  HTMLAttributes: {
    class: 'mention',
  },
  renderText({ options, node }) {
    return `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`;
  },
  renderHTML({ options, node }) {
    return [
      'span',
      mergeAttributes(options.HTMLAttributes, {
        'data-type': 'mention',
        'data-id': node.attrs.id,
        'data-label': node.attrs.label ?? '',
      }),
      `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`,
    ];
  },
  suggestion: mentionSuggestion,
});

type ButtonProps = {
  onClick: () => void;
  icon?: React.ReactNode;
  label?: string;
  sx?: React.CSSProperties;
};

const renderButton = ({ onClick, icon, label, sx }: ButtonProps) => {
  return (
    <Button
      onClick={onClick}
      variant="contained"
      color="inherit"
      size="small"
      sx={{
        ...(sx || {}),
        flexShrink: 0,
        paddingLeft: '5px',
        paddingRight: '5px',
        paddingTop: '6px',
        paddingBottom: '6px',
        minWidth: '30px',
        minHeight: '30px',
      }}
    >
      {icon}
      {icon && label ? <span style={{ marginLeft: '5px' }} /> : null}
      {label}
    </Button>
  );
};

type TiptapEditorProps = {
  value: string;
  onChange: (value: string) => void;
  style?: React.CSSProperties;
  renderExtraContent?: (editor: Editor) => React.ReactNode;
  onChangeSelection?: ({ from, to }: { from: number; to: number }) => void;
  showPlaceholderChooser?: boolean;
  onSelectPlaceholder?: (value: string) => boolean;
  showMentions?: boolean;
  enableMarkdown?: boolean;
  compact?: boolean;
  disabled?: boolean;
};

const TiptapEditor = ({
  value,
  onChange,
  style,
  renderExtraContent,
  onChangeSelection,
  showPlaceholderChooser,
  showMentions = false,
  enableMarkdown = false,
  compact,
  disabled,
  onSelectPlaceholder,
}: TiptapEditorProps) => {
  const theme = useTheme();
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiPrompt, setAIPrompt] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiResult, setAIResult] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      Link.configure({
        openOnClick: true,
      }),
      ResizableImage,
      ...(showMentions ? [mentionExtension] : []),
      ...(enableMarkdown ? [Markdown] : []),
    ],
    immediatelyRender: false,
    editable: !disabled,
    // Markdown shortcuts (e.g. **bold**, # heading) only when explicitly enabled.
    enableInputRules: enableMarkdown,
    enablePasteRules: enableMarkdown,
    content: value,
    ...(enableMarkdown
      ? {
          contentType: isHtmlContent(value) ? ('html' as const) : ('markdown' as const),
        }
      : {}),
    onUpdate: ({ editor }) => {
      // Mentions need HTML so data-id (userId) is preserved for notifications.
      if (enableMarkdown && !showMentions) {
        onChange(editor.getMarkdown());
        return;
      }

      onChange(editor.getHTML().replace(/<p><\/p>/g, '<br>'));
    },
    onSelectionUpdate: ({ editor }) => {
      const { from, to } = editor.state.selection;

      if (onChangeSelection) {
        onChangeSelection({ from, to });
      }
    },
    editorProps: {
      handlePaste: (view, event, slice) => {
        void view;
        void slice;
        const clipboardData = event.clipboardData;
        const htmlData = clipboardData?.getData('text/html');

        if (htmlData && htmlData.trim() !== '' && isMsWordClipboardHtml(htmlData)) {
          try {
            if (editor) {
              editor.commands.insertContent(cleanMsWordHtml(htmlData));
              return true;
            }
            return false;
          } catch (error) {
            console.error('Error processing HTML paste:', error);
            return false;
          }
        }

        return false;
      },
    },
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  const isTextSelected = () => {
    if (!editor) return false;

    const { from, to } = editor.state.selection;

    return from !== to;
  };

  const getSelectedHTML = (): string => {
    if (!editor) return '';

    const { from, to } = editor.state.selection;

    if (from !== to) {
      // Serialize the exact selection to HTML using ProseMirror's DOMSerializer
      const slice = editor.state.selection.content();
      const serializer = DOMSerializer.fromSchema(editor.schema);
      const fragment = serializer.serializeFragment(slice.content);
      const container = document.createElement('div');
      container.appendChild(fragment);
      return container.innerHTML;
    } else {
      return editor.getHTML();
    }
  };

  const askAI = () => {
    setIsAnalyzing(true);
    setAIResult('');

    sendApiRequest<{ text: string }>({
      path: `/ai-editor/analyze-content`,
      method: 'POST',
      data: {
        prompt: aiPrompt,
        content: getSelectedHTML(),
      },
    })
      .then(response => {
        setIsAnalyzing(false);
        setAIResult(response.text);
      })
      .catch(() => {
        setIsAnalyzing(false);
      });
  };

  const addPlaceholderToEditor = (value: string) => {
    if (editor) {
      const { from, to } = editor.state.selection;

      if (from !== to) {
        editor.chain().focus().insertContentAt({ from, to }, value).run();
      } else {
        editor.commands.insertContent(value);
      }
    }
  };

  const useAIResult = () => {
    if (!editor) return;

    const { from, to } = editor.state.selection;

    if (from !== to) {
      // Replace the selected text with new content
      editor.chain().focus().insertContentAt({ from, to }, aiResult).run();
    } else {
      editor.commands.setContent(aiResult);
    }

    setAIPrompt('');
    setAIResult('');
    setShowAIAssistant(false);

    onChange(enableMarkdown ? editor.getMarkdown() : aiResult);
  };

  const renderAIPrompt = () => {
    if (!showAIAssistant) {
      return null;
    }

    return (
      <Modal
        open={showAIAssistant}
        onClose={() => {
          setShowAIAssistant(false);
          setAIPrompt('');
          setAIResult('');
        }}
        title={aiResult ? 'Result' : 'AI Assistant'}
        renderContent={() => (
          <>
            {aiResult ? (
              <Box display="flex" gap={2} sx={{ marginTop: '-30px' }}>
                <Box flex={1} sx={{ borderRight: `1px solid ${theme.palette.divider}`, paddingRight: '10px' }}>
                  <h3>Before:</h3>
                  <div
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(isTextSelected() ? getSelectedHTML() : value) }}
                  />
                </Box>

                <Box flex={1}>
                  <h3>After:</h3>
                  <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(aiResult) }} />
                </Box>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {isTextSelected() ? 'Selected' : 'Content'}
                  </Typography>
                  <Box
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '4px',
                      padding: '8px',
                      maxHeight: 160,
                      overflow: 'auto',
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(isTextSelected() ? getSelectedHTML() : value),
                      }}
                    />
                  </Box>
                </Box>

                <TextField
                  placeholder="Tell the AI what to do (e.g., Rewrite this text to be shorter, more direct, and impactful without losing the key message)"
                  value={aiPrompt}
                  onChange={e => setAIPrompt(e.target.value)}
                  multiline
                  fullWidth
                  size="small"
                  minRows={8}
                />
              </Box>
            )}
          </>
        )}
        renderActions={() => (
          <>
            {aiResult ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button variant="contained" color="primary" onClick={useAIResult}>
                  Replace
                </Button>
              </Box>
            ) : (
              <Button variant="contained" color="primary" disabled={isAnalyzing} onClick={() => askAI()}>
                Rewrite
                {isAnalyzing && <CircularProgress color="secondary" size={20} sx={{ marginLeft: '10px' }} />}
              </Button>
            )}
          </>
        )}
      />
    );
  };

  const toolbarStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    background: theme.palette.action.hover,
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    border: `1px solid ${theme.palette.divider}`,
  };

  if (compact) {
    toolbarStyle.overflowX = 'auto';
    toolbarStyle.maxWidth = '100%';
    toolbarStyle.flexWrap = 'nowrap';
  }

  return (
    <>
      {/* Toolbar */}
      {!disabled && (
        <div style={toolbarStyle}>
          {renderButton({
            onClick: () => editor?.chain().focus().toggleBold().run(),
            icon: <FormatBoldIcon fontSize="small" />,
            sx: { fontWeight: 'bold' },
          })}

          {renderButton({
            onClick: () => editor?.chain().focus().toggleItalic().run(),
            icon: <FormatItalicIcon fontSize="small" />,
            sx: { fontStyle: 'italic' },
          })}

          {renderButton({
            onClick: () => editor?.chain().focus().toggleStrike().run(),
            icon: <StrikethroughSIcon fontSize="small" />,
            sx: { textDecoration: 'line-through' },
          })}

          {renderButton({
            onClick: () => {
              const currentUrl = (editor?.getAttributes('link').href as string) || '';
              const url = window.prompt('URL', currentUrl);
              if (url) {
                editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
              }
            },
            icon: <InsertLinkIcon fontSize="small" />,
          })}

          {renderButton({
            onClick: () => editor?.chain().focus().unsetLink().run(),
            icon: <LinkOffIcon fontSize="small" />,
          })}

          {renderButton({
            onClick: () => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.onchange = e => {
                const file = (e.target as HTMLInputElement).files?.[0];

                if (!file) return;

                if (file.size > 5 * 1024 * 1024) {
                  notify('File size must be under 5MB');
                  return;
                }

                const formData = new FormData();
                formData.append('file', file);

                setIsUploadingImage(true);

                uploadFileRequest({ formData })
                  .then(response => response.json())
                  .then(({ result }: { result?: string }) => {
                    const src = result ? readFile(result) : undefined;

                    if (!src) {
                      return;
                    }

                    if (editor) {
                      // Insert image directly without showing dimensions popup
                      editor
                        .chain()
                        .focus()
                        .setResizableImage({
                          src,
                          alt: file.name,
                          title: file.name,
                          'data-keep-ratio': true,
                        })
                        .run();
                    }
                  })
                  .catch(error => {
                    notify('Could not upload image. Please try again.');
                    console.error(error);
                  })
                  .finally(() => {
                    setIsUploadingImage(false);
                  });
              };
              input.click();
            },
            icon: isUploadingImage ? (
              <CircularProgress color="secondary" size={16} />
            ) : (
              <AddPhotoAlternateIcon fontSize="small" />
            ),
            label: isUploadingImage ? 'Uploading...' : '',
          })}

          {renderButton({
            onClick: () => editor?.chain().focus().toggleHeading({ level: 1 }).run(),
            label: 'H1',
          })}

          {renderButton({
            onClick: () => editor?.chain().focus().toggleHeading({ level: 2 }).run(),
            label: 'H2',
          })}

          {renderButton({
            onClick: () => editor?.chain().focus().toggleBulletList().run(),
            icon: <FormatListBulletedIcon fontSize="small" />,
          })}

          {renderButton({
            onClick: () => editor?.chain().focus().toggleOrderedList().run(),
            icon: <FormatListNumberedIcon fontSize="small" />,
          })}

          {renderButton({
            onClick: () => editor?.chain().focus().undo().run(),
            icon: <UndoIcon fontSize="small" />,
          })}

          {renderButton({
            onClick: () => editor?.chain().focus().redo().run(),
            icon: <RedoIcon fontSize="small" />,
          })}

          {renderButton({
            onClick: () => setShowAIAssistant(true),
            icon: <AutoFixHighIcon fontSize="small" />,
            label: 'AI Assistant',
          })}

          {showPlaceholderChooser && (
            <PlaceholderChooser
              onSelect={value => {
                if (onSelectPlaceholder) {
                  const isValid = onSelectPlaceholder(value);

                  if (!isValid) {
                    return;
                  }
                }

                addPlaceholderToEditor(value);
              }}
            />
          )}

          {renderExtraContent && editor && renderExtraContent(editor)}
        </div>
      )}

      {/* Editor */}
      <Box
        sx={{
          '& .ProseMirror .mention': theme => mentionChipSx(theme),
        }}
      >
        <EditorContent
          editor={editor}
          style={{
            border: `1px solid ${theme.palette.divider}`,
            padding: '16px',
            borderRadius: '8px',
            background: theme.palette.background.paper,
            fontSize: '13px',
            lineHeight: '1.8',
            overflow: 'auto',
            ...(disabled
              ? {}
              : {
                  marginTop: '-1px',
                  borderTopRightRadius: '0',
                  borderTopLeftRadius: '0',
                }),
            ...style,
          }}
        ></EditorContent>
      </Box>

      {!disabled ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            mt: 0.75,
            px: 0.25,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', mt: '1px', flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            Enter starts a new paragraph (adds space between lines). Shift+Enter inserts a line break without the extra
            space.
          </Typography>
        </Box>
      ) : null}

      {renderAIPrompt()}
    </>
  );
};

export default TiptapEditor;
